<template>
  <!-- Error Message -->
  <PageError v-if="error"
   :error="error"
   :errorCode="errorCode"
  />

  <div v-else-if="canViewLoadshedding || user?.is_staff" class="p-6 dark:text-white">
    <div class="flex items-center justify-between mb-6">
      <h1 class="text-3xl font-bold text-gray-900 dark:text-white">
        Loadshedding - {{ filteredLoadsheddings.length }}
        <span v-if="isFiltered" class="text-sm text-gray-500">(filtered)</span>
      </h1>
      <div class="mb-4 text-base flex space-x-3 justify-between">
        <div class="relative group">
        <button
          type="button"
           class="hover:bg-transparent bg-transparent"
          @click="startTutorial"
        >
        <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-circle-question-mark dark:text-gray-50 text-gray-900"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><path d="M12 17h.01"/></svg>
        </button>
          <span class="absolute top-full text-xs right-1/2 -translate-y-1/2 glass-card mb-1 px-2 py-0.5 glass-card opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap">Tutorial</span>
        </div>
        <button v-if="canAddLoadshedding || user?.is_staff"
          id="add-loadshedding-btn"
          @click="goToAddLoadshedding"
          class="btn bg-blue-700 text-white hover:bg-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          Add Loadshedding Area
        </button>
      </div>
    </div>

    <!-- Organization Tabs -->
    <OrganizationTabs
      v-if="user?.is_staff || isServiceProviderUser"
      :organizations="tenant.organizations.value"
      :selected-org-id="tenant.currentOrganizationId.value"
      :loading="tenant.isLoading.value"
      :show-tabs="true"
      loading-text="Loading Loadshedding Areas..."
      @select="handleOrgSelect"
    />
    
    <div
      class="flex flex-col md:flex-row md:items-center md:justify-between mb-6"
    >
      <div class="mb-4 md:mb-0 md:flex-1 md:mr-4">
        <SearchBar
          v-model="searchQuery"
          placeholderText="Search loadsheddings by name..."
        />
      </div>

  
       <!-- Bulk Action Section -->
       <div class="flex items-center mt-4 md:mt-0 space-x-2">
        <GlassSelect
          v-model="bulkAction"
          :options="canDeleteLoadshedding || user?.is_staff ? [{ value: 'delete', label: 'Delete selected items' }] : []"
          placeholder="Bulk Actions"
          :valueKey="'value'"
          :labelKey="'label'"
        />
        <button
          @click="applyBulkAction"
          :disabled="!bulkAction || selectedLoadsheddings.length === 0"
          :class="[
            'btn focus:outline-none focus:ring-2 focus:ring-blue-500',
            bulkAction && selectedLoadsheddings.length
              ? 'bg-red-700 text-white hover:bg-red-800'
              : 'bg-gray-300 text-gray-500 cursor-not-allowed',
          ]"
        >
          Apply
        </button>
      </div>
    </div>

    <GenericTable
      id="loadsheddings-table"
      ref="table"
      :columns="columns"
      :items="paginatedLoadsheddings"
      itemKey="id" 
      :selectable="true"
      @update:selectedItems="updateSelectedLoadsheddings"
      @rowClick="goToLoadsheddingDetail"
    >
      <template #org="{ value }">
        {{ tenant.getOrganizationName(value) }}
      </template>
      <template #created_date="{ value }">
        {{ formatDateTime(value) }}
      </template>
    </GenericTable>

    <div
      class="flex flex-col md:flex-row items-center justify-between mt-8 space-y-4 md:space-y-0"
    >
      <div class="flex items-center space-x-2">
        <button
          @click="changePage(-1)"
          :disabled="currentPage === 1"
          class="px-4 py-2 rounded-md bg-gray-800 text-gray-100 hover:bg-gray-900 disabled:opacity-50 disabled:cursor-not-allowed transition duration-200"
        >
          Previous
        </button>
        <button
          @click="changePage(1)"
          :disabled="currentPage === totalPages"
          class="px-4 py-2 rounded-md bg-gray-800 text-gray-100 hover:bg-gray-900 disabled:opacity-50 disabled:cursor-not-allowed transition duration-200"
        >
          Next
        </button>
      </div>
      <div class="text-gray-700 dark:text-white">
        Page {{ currentPage }} of {{ totalPages }}
      </div>
      <div>
        <GlassSelect
          v-model="itemsPerPage"
          :options="[{ value: 10, label: '10 per page' }, { value: 20, label: '20 per page' }, { value: 50, label: '50 per page' }, { value: 100, label: '100 per page' }, { value: -1, label: 'All' }]"
          :valueKey="'value'"
          :labelKey="'label'"
          placeholder="Items per page"
          @change="updatePagination"
        />
      </div>
    </div>

    <DeleteModal
      v-if="deletingItem"
      :deleting-item="deletingItem"
      :componet-objects="selectedObjects"
      :is-deleting="isDeleting"
      @delete="confirmDelete"
      @close="deletingItem=false"
      :isChildObject="true"
      :count="count"
    />

  </div>
</template>

<script>
import {
  getAllLoadsheddings,
  deleteLoadshedding,
} from "@/services/loadshedding-service";
import SearchBar from "@/components/SearchBar.vue";
import GenericTable from "@/components/GenericTable.vue";
import PageError from "@/components/PageError.vue";
import { useStore } from "@/store";
import DeleteModal from "@/components/DeleteModal.vue";
import OrganizationTabs from "@/components/OrganizationTabs.vue";
import { useMultiTenant } from "@/composables/useMultiTenant";
import GlassSelect from "@/components/GlassSelect.vue";
import { useTutorial } from "@/composables/tutorial";

export default {
  name: "LoadsheddingView",
  components: {
    SearchBar,
    GenericTable,
    PageError,
    DeleteModal,
    OrganizationTabs,
    GlassSelect,
  },
  setup() {
    const tenant = useMultiTenant();
    const { start } = useTutorial();
    return { tenant, _start: start };
  },
  data() {
    return {
      loadsheddings: [],
      sites: {},
      searchQuery: "",
      bulkAction: "",
      filteredLoadsheddings: [],
      currentPage: 1,
      itemsPerPage: 10,
      selectedLoadsheddings: [],
      deletingItem: false,
      columns: [
        { key: "name", label: "Name", sortable: true, width: "20%" },
        { key: "area_id", label: "Area ID", sortable: true, width: "20%" },
        { key: "org", label: "Organisation", sortable: true, width: "20%" },
        { key: "site_name", label: "Site", sortable: true, width: "20%" },
        {
          key: "created_date",
          label: "Created Date",
          sortable: true,
          width: "20%",
        },
      ],
      canAddLoadshedding: true,
      canDeleteLoadshedding: true,
      canViewLoadshedding: true,
      error: "",
      loadsheddingTutorialSteps: [
        {
          element: '#searchbar',
          action: 'display',
          message: 'Search loadshedding areas by name.'
        },
        {
          element: '#add-loadshedding-btn',
          action: 'display',
          message: 'Click here to create a new loadshedding area.'
        },
        {
          element: '#loadsheddings-table',
          action: 'display',
          message: 'Click any row to view or edit loadshedding area details.'
        },
      ],
      errorCode: null,
      errorMessages: {
        400 : "Bad Request .The server could not understand the request.",
        401 : "Unauthorized access. Please check your authentication token or log in again.",
        403 : "You do not have permission to access this resource. Contact a staff member to assign you the permissions to access this page.",
        404 : "Page Not Found.",
        500 : "Internal Server Error - Something went wrong on the server.",
        422 : "Malformed request. Failed to load Loadshedding Areas!"
      },
      isDeleting: false
    };
  },
  computed: {
    user() {
      return useStore.getUser();
    },
    isServiceProviderUser() {
      return useStore.isServiceProviderUser();
    },
    isFiltered() {
      return this.searchQuery !== "";
    },
    totalPages() {
      return this.itemsPerPage === -1
        ? 1
        : Math.ceil(this.filteredLoadsheddings.length / this.itemsPerPage);
    },
    paginatedLoadsheddings() {
      if (this.itemsPerPage === -1) {
        return this.filteredLoadsheddings;
      }
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.filteredLoadsheddings.slice(start, end);
    },
    selectedObjects(){
        return this.selectedLoadsheddings.map(loadshedding => ({
          name: "Loadshedding Area",
          value: loadshedding.name,
       }));
    },
    count(){
      return [{name: "Loadshedding Areas", value: this.selectedObjects.length}]
    }
  },
  watch: {
    searchQuery() {
      this.applyFilters();
    },
    user: {
      handler(user) {
        if (user) {
          this.checkPermissions();
        }
      },
      immediate: true
    },
  },

  async mounted() {
    this.checkPermissions();
    await this.tenant.fetchOrganizations();

    if (this.tenant.currentOrganizationId.value) {
        await this.tenant.syncUrlParam();
        await this.fetchLoadsheddings(this.tenant.currentSchemaName.value);
    } else {
      await this.tenant.loadInitialData(this.fetchLoadsheddings);
    }
  },
  methods: {
    async startTutorial() {
      await this._start(this.loadsheddingTutorialSteps);
    },
    async checkPermissions(){
      if (this.user) {
        this.canAddLoadshedding = await useStore.hasPermission("Can add loadshedding area");
        this.canDeleteLoadshedding = await useStore.hasPermission("Can delete loadshedding area");
        this.canViewLoadshedding = await useStore.hasPermission("Can view loadshedding area");
      }
    },
    async handleOrgSelect(orgId) {
      try {
        this.error = "";
        await this.tenant.switchOrganization(orgId, this.fetchLoadsheddings);
      } catch (error) {
        this.error = "Failed to switch organization";
        console.error('Organization select error:', error);
      }
    },
    async applyBulkAction() {
      if (this.bulkAction === "delete" ) {
        this.deletingItem = true;
      } 
      this.bulkAction = "";
    },
    async confirmDelete() {
      this.isDeleting = true;
      await this.deleteSelectedLoadsheddings();
      this.deletingItem = false; 
      this.bulkAction = "";
    },
    async deleteSelectedLoadsheddings() {
      if (this.selectedLoadsheddings.length === 0) {
        alert("No loadshedding selected for deletion");
        return;
      }
      try {
        const schemaName = this.tenant.currentSchemaName.value;
        if (!schemaName) {
          this.error = "Malformed request. Failed to delete selected Loadshedding Areas!";
          this.errorCode = "422";
          return;
        }
        const deletePromises = this.selectedLoadsheddings.map((loadshedding) =>
          deleteLoadshedding(loadshedding.id, schemaName)
        );
        await Promise.all(deletePromises);
        await this.fetchLoadsheddings(schemaName);
        this.$refs.table.clearSelection();
        this.applyFilters();
        this.selectedLoadsheddings = [];
      } catch (error) {
        console.error("Error during bulk delete:", error);
        alert("An error occurred while deleting Loadsheddings. Please try again.");
      }  finally {
        this.isDeleting = false;
      }
    },
    async fetchLoadsheddings(schemaName = null) {
      try {
        const loadsheddings = await getAllLoadsheddings(schemaName);
        this.loadsheddings = [...loadsheddings];
        this.applyFilters();
      } catch (error) {
        if (error.response) {
          this.error = `${this.errorMessages[error.response.status]}`;
          this.errorCode = `${error.response.status}`;
        } else{
          this.error = `Unknown Error`
        }
      }
    },
    goToLoadsheddingDetail(loadshedding) {
      this.$router.push({
        name: "LoadsheddingDetails",
        params: { id: loadshedding.id },
        query: this.tenant.addSchemaParam()
      });
    },
    goToAddLoadshedding() {
      this.$router.replace({ 
        name: "AddLoadshedding",
        query: this.tenant.addSchemaParam()
      });
    },
    applyFilters() {
      this.filteredLoadsheddings = this.loadsheddings.filter((loadshedding) =>
        loadshedding.name.toLowerCase().includes(this.searchQuery.toLowerCase())
      );
      this.currentPage = 1;
    },
    updateSelectedLoadsheddings(selectedItems) {
      this.selectedLoadsheddings = selectedItems.filter((item => !item.marked_for_deletion));
    },
    formatDateTime(dateString) {
      const options = {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        timeZoneName: "short",
      };
      return new Date(dateString).toLocaleString(undefined, options);
    },
    changePage(delta) {
      this.currentPage += delta;
    },
    updatePagination() {
      this.currentPage = 1;
    },
  },
};
</script>
